import React, {useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {Divider, makeStyles} from '@material-ui/core'
import useLangEn from 'hooks/useLangEn'
import {ResMCI} from 'api/languageApi'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import AIDementiaPredictionGuide from './AIDementiaPredictionGuide'

const useStyles = makeStyles(HealthCenterReportStyle)

export interface AIDementiaPredictionProps {
  mciResultImage: string
  mciProbability: number
  name: string
  textMCI: ResMCI
  footer: string
}

const AIDementiaPredictionV1 = ({
  mciResultImage,
  mciProbability,
  name,
  textMCI,
  footer,
}: AIDementiaPredictionProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const isEm = useLangEn()
  const refs = useRef<HTMLDivElement[]>([])
  const formatText = (template: string, values: (string | number)[]) => {
    return template.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }

  const data = useMemo(() => {
    const data = textMCI.section.prediction.opinions.recommendation_table
    return [
      {
        index: 1,
        stepText: `${data.stage1.stage.number} (~50%)`,
        title: data.stage1.stage.description,
        content: data.stage1.recommendations,
        color: '#15A87A',
        isActive: mciProbability < 50,
      },
      {
        index: 2,
        stepText: `${data.stage2.stage.number} (50~60%)`,
        title: data.stage2.stage.description,
        content: data.stage2.recommendations,
        color: '#63C54B',
        isActive: mciProbability >= 50 && mciProbability < 60,
      },
      {
        index: 3,
        stepText: `${data.stage3.stage.number} (60~70%)`,
        title: data.stage3.stage.description,
        content: data.stage3.recommendations,
        color: '#EBC600',
        isActive: mciProbability >= 60 && mciProbability < 70,
      },
      {
        index: 4,
        stepText: `${data.stage4.stage.number} (70~85%)`,
        title: data.stage4.stage.description,
        content: data.stage4.recommendations,
        color: '#E3A037',
        isActive: mciProbability >= 70 && mciProbability < 85,
      },
      {
        index: 5,
        stepText: `${data.stage5.stage.number}  (85%~)`,
        title: data.stage5.stage.description,
        content: data.stage5.recommendations,
        color: '#D14126',
        isActive: mciProbability >= 85,
      },
    ]
  }, [textMCI, mciProbability, isEm])
  useEffect(() => {
    if (textMCI) {
      textMCI.section.prediction.opinions.annotation.forEach((item, index) => {
        if (refs.current[index]) {
          refs.current[index].innerHTML = item
        }
      })
    }
  }, [textMCI])
  return (
    <div className={classes.brainBasic}>
      <WrapElement number={1} text={textMCI.section.prediction.title}>
        <div className={classes.contentCard}>
          <img
            className={classes.mciResultImage}
            src={mciResultImage}
            alt='mci'
          />
        </div>
        <div className={classes.mciResultDes}>
          <div className={classes.mciResultDesTitle}>
            {formatText(textMCI.section.prediction.opinions.diagnosis, [
              name,
              data.find((item) => item.isActive)?.index || 1,
            ])}
          </div>
          <div className={classes.mciResultDesContent}>
            <div className={classes.mciResultDesContentList}>
              <div className={classes.mciResultDesContentText}>
                <span>1</span>
                <div style={{width: '99%'}}>
                  {textMCI.section.prediction.opinions.text_box.circle1}
                </div>
              </div>
              <div className={classes.mciResultDesContentText}>
                <span>2</span>
                <div style={{width: '99%'}}>
                  {textMCI.section.prediction.opinions.text_box.circle2}
                </div>
              </div>
            </div>
            <div className={classes.mciResultDesContentText}>
              {textMCI.section.prediction.opinions.text_box.explanation}
            </div>
          </div>
          <div className={classes.AIDementiaPredictionDetail}>
            {data.map((content) => (
              <AIDementiaPredictionGuide
                key={content.index}
                stepText={content.stepText}
                color={content.color}
                title={content.title}
                content={content.content}
                isActive={content.isActive}
              />
            ))}
          </div>
          <div className={classes.mciResultNote}>
            {textMCI.section.prediction.opinions.annotation.map((_, index) => (
              <div
                className={classes.mciResultNoteElm}
                key={index}
                ref={(el) => {
                  if (el) refs.current[index] = el
                }}
              />
            ))}
          </div>
        </div>
        <Divider style={{marginLeft: 10, marginRight: 10, marginBottom: 10}} />
        <div className={classes.footer}>{footer}</div>
      </WrapElement>
    </div>
  )
}

export default AIDementiaPredictionV1
